import { ChangeEvent, useEffect, useState } from 'react';
import { Search } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { AddCouponMutation as AddCoupon } from '__generated__/graphql';
import { isEmpty } from '../../../../common/helpers';
import AddCouponForm from './AddCouponForm';
import CouponList from './CouponList';
import GetCouponQuery from './GetCouponQuery';
import GetCouponsQuery from './GetCouponsQuery';
import UI from '../../../../common/UI';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useUrlState from '../../../../common/useUrlState';

type AddedCoupon = AddCoupon['addCoupon'];

interface CouponsPageInterface {
  eventId: string;
  couponId?: string;
}

interface SearchPaginationParams {
  search: string | null;
}

const CouponsPage = ({ eventId, couponId }: CouponsPageInterface) => {
  const { t } = useTranslation();
  const history = useHistory();
  const project = useProject();

  const [filtersKey, setFiltersKey] = useState(0);

  const [urlState, setUrlState] = useUrlState<SearchPaginationParams>({
    parse: (rawParams, { string }) => ({
      search: string(rawParams.search),
    }),
    onChange: () => {
      // Rerender the filters when user clicks on 'Registrations' in the nav bar.
      setFiltersKey((key) => key + 1);
    },
  });

  const { data, refetch, loading } = useQuery(
    GetCouponsQuery,
    {
      variables: {
        ...urlState,
        project_id: project.id,
        event_id: eventId,
      },
    },
  );

  const event = data?.project.event;

  const { data: couponData, loading: couponLoading } = useQuery(
    GetCouponQuery,
    {
      variables: {
        event_id: eventId,
        coupon_id: couponId,
      },
      skip: !couponId,
    },
  );

  const [openAddCouponModal, closeAddCouponModal] = useModal(() => {
    const handleSuccess = (coupon: AddedCoupon) => {
      closeAddCouponModal();
      history.push(`/${project.id}/events/${event.id}/coupons/${coupon.id}`);
    };

    return (
      <UI.Modal maxWidth={600} close={closeAddCouponModal}>
        <AddCouponForm
          onSuccess={handleSuccess}
          event={event}
        />
      </UI.Modal>
    );
  }, [event]);

  useEffect(() => {
    refetch();
  }, [couponId, refetch]);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    // Search input is debounced so we can update params immediately
    setUrlState((oldParams) => ({ ...oldParams, search: event.target.value || null }), true);
  };

  // Filter helpers
  const hasFilters = Object.values(urlState).some((param) => !isEmpty(param));

  const resetFilters = () => {
    setUrlState({ search: null });
  };

  // Either we have coupons, or we have no coupons when filters are enabled
  const hasCoupons = event?.coupons.length > 0 || (event?.coupons.length === 0 && hasFilters);

  if (!event) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.FlexContainer sc={{ justifyContent: 'space-between', direction: ['column', 'row'] }}>
          <UI.SectionHeading>
            {t('coupons')}
          </UI.SectionHeading>

          {hasCoupons && (
            <UI.FlexContainer sc={{ spaceX: 1 }}>
              <UI.Button
                sc={{ brand: 'secondary' }}
                onClick={() => openAddCouponModal()}
              >
                {t('add_coupon')}
              </UI.Button>
            </UI.FlexContainer>
          )}
        </UI.FlexContainer>

        <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'] }}>
          <UI.Div>
            <FilterBar
              onSearch={handleSearch}
              search={urlState.search}
              key={filtersKey}
            />
          </UI.Div>

          <UI.Div>
            {!hasCoupons && (
              <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}>
                <UI.H3 sc={{ mb: 2 }}>{t('add_coupons')}</UI.H3>
                <UI.Paragraph>{t('coupons_get_started')}</UI.Paragraph>
                <UI.Button
                  onClick={() => openAddCouponModal()}
                  sc={{ brand: 'secondary' }}
                >
                  {t('add_a_coupon')}
                </UI.Button>
              </UI.Card>
            )}
            {hasCoupons && (
              <UI.Div sc={{ loading }}>
                <CouponList
                  event={event}
                  onResetFilters={() => resetFilters()}
                  activeCouponId={couponId}
                  // Always pass a fresh data object
                  couponData={couponData?.coupon.id === couponId && !couponLoading ? couponData : null}
                />
              </UI.Div>
            )}
          </UI.Div>
        </UI.GridContainer>
      </UI.GridContainer>
    </UI.Section>
  );
};

interface FilterBarProps {
  search: string;
  onSearch: (event: ChangeEvent<HTMLInputElement>) => void;
}

const FilterBar = ({ search, onSearch }: FilterBarProps) => {
  const { t } = useTranslation();

  return (
    <UI.InputGroup sc={{ mb: 0 }}>
      <UI.InputWrapper>
        <span>
          <UI.Icon>
            <Search />
          </UI.Icon>
        </span>
        <UI.DebouncedInput
          type="text"
          value={search}
          onChange={onSearch}
          placeholder={t('search_coupon_codes')}
          sc={{ pl: 5 }}
        />
      </UI.InputWrapper>
    </UI.InputGroup>
  );
};

export default CouponsPage;
